import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Branch, BranchFormData } from "./branch";
import { branchService } from "./branchService";
import { USE_STUB_DATA, STUB_BRANCHES } from "../../config/stubData";
import { isSuccessResponse } from "../../services/enhancedApiClient";
import errorHandlingService from "../../services/errorHandlingService";

interface BranchState {
  branches: Branch[];
  selectedBranch: Branch | null;
  loading: boolean;
  error: string | null;
}

const initialState: BranchState = {
  branches: USE_STUB_DATA ? STUB_BRANCHES : [],
  selectedBranch: null,
  loading: false,
  error: null,
};

// Using centralized stub data from config/stubData.ts

// Async Thunks
export const fetchBranches = createAsyncThunk(
  "branches/fetchBranches",
  async (_, { rejectWithValue, getState }) => {
    try {
      if (USE_STUB_DATA) {
        return STUB_BRANCHES;
      } else {
        const response = await branchService.getBranches();
        
        // Check if response is successful using the new API response format
        if (isSuccessResponse(response)) {
          // Transform API data to match local Branch interface
          const transformedBranches: Branch[] = response.data.branches.map(
            (apiBranch: any) => ({
              id: apiBranch.id,
              name: apiBranch.name,
              code: apiBranch.code || `BR${apiBranch.id}`,
              address: apiBranch.address,
              city: apiBranch.city,
              state: apiBranch.state,
              zipCode: apiBranch.postal_code || apiBranch.zipCode || "",
              phone: apiBranch.phone,
              email: apiBranch.email,
              status: apiBranch.status === "active" || apiBranch.status,
              managerName: apiBranch.manager?.first_name
                ? `${apiBranch.manager.first_name} ${
                    apiBranch.manager.last_name || ""
                  }`
                : (apiBranch.managerName || "Not Assigned"),
              managerPhone: apiBranch.managerPhone || "",
              managerEmail:
                apiBranch.manager?.email || apiBranch.managerEmail || "",
              workingHours: apiBranch.workingHours || {
                start: "09:00",
                end: "18:00",
              },
              createdAt: apiBranch.created_at || apiBranch.createdAt,
              updatedAt: apiBranch.updated_at || apiBranch.updatedAt,
            })
          );
          return transformedBranches;
        } else {
          // Handle error response - this will be processed by the centralized error handling
          const errorMessage = response.error?.message || "Failed to fetch branches";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      // Get userId from Redux state instead of letting service access store
      const state = getState() as any;
      const userId = state.auth?.user?.id;
      
      // Use centralized error handling for any unexpected errors
      await errorHandlingService.handleApiError(
        error,
        "Fetch Branches",
        "BranchSlice",
        userId
      );
      
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch branches"
      );
    }
  }
);

export const fetchBranchById = createAsyncThunk(
  "branches/fetchBranchById",
  async (branchId: string, { rejectWithValue, getState }) => {
    try {
      if (USE_STUB_DATA) {
        const branch = STUB_BRANCHES.find((b) => b.id === branchId);
        if (!branch) throw new Error("Branch not found");
        return branch;
      } else {
        const response = await branchService.getBranch(branchId);
        
        if (isSuccessResponse(response)) {
          const apiBranch: any = response.data;
          // Transform API data to match local Branch interface
          const transformedBranch: Branch = {
            id: apiBranch.id,
            name: apiBranch.name,
            code: apiBranch.code || `BR${apiBranch.id}`,
            address: apiBranch.address,
            city: apiBranch.city,
            state: apiBranch.state,
            zipCode: apiBranch.postal_code || apiBranch.zipCode || "",
            phone: apiBranch.phone,
            email: apiBranch.email,
            status: apiBranch.status === "active" || apiBranch.status,
            managerName: apiBranch.manager?.first_name
              ? `${apiBranch.manager.first_name} ${
                  apiBranch.manager.last_name || ""
                }`
              : (apiBranch.managerName || "Not Assigned"),
            managerPhone: apiBranch.managerPhone || "",
            managerEmail:
              apiBranch.manager?.email || apiBranch.managerEmail || "",
            workingHours: apiBranch.workingHours || {
              start: "09:00",
              end: "18:00",
            },
            createdAt: apiBranch.created_at || apiBranch.createdAt,
            updatedAt: apiBranch.updated_at || apiBranch.updatedAt,
          };
          return transformedBranch;
        } else {
          const errorMessage = response.error?.message || "Failed to fetch branch";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      // Get userId from Redux state instead of letting service access store
      const state = getState() as any;
      const userId = state.auth?.user?.id;
      
      await errorHandlingService.handleApiError(
        error,
        "Fetch Branch By ID",
        "BranchSlice",
        userId
      );
      
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch branch"
      );
    }
  }
);

export const createBranch = createAsyncThunk(
  "branches/createBranch",
  async (branchData: BranchFormData, { rejectWithValue }) => {
    try {
      if (USE_STUB_DATA) {
        const newBranch: Branch = {
          ...branchData,
          id: Math.random().toString(),
          managerName: branchData.managerName || "Not Assigned",
          managerPhone: branchData.managerPhone || "",
          managerEmail: branchData.managerEmail || "",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return newBranch;
      } else {
        const response = await branchService.createBranch(branchData);
        
        if (isSuccessResponse(response)) {
          const apiBranch: any = response.data;
          // API now returns camelCase, so minimal transformation needed
          const transformedBranch: Branch = {
            id: apiBranch.id,
            name: apiBranch.name,
            code: apiBranch.code,
            address: apiBranch.address,
            city: apiBranch.city,
            state: apiBranch.state,
            zipCode: apiBranch.zipCode || "",
            phone: apiBranch.phone,
            email: apiBranch.email,
            status: apiBranch.status,
            managerName: apiBranch.managerName || "Not Assigned",
            managerPhone: apiBranch.managerPhone || "",
            managerEmail: apiBranch.managerEmail || "",
            workingHours: apiBranch.workingHours || {
              start: "09:00",
              end: "18:00",
            },
            createdAt: apiBranch.createdAt,
            updatedAt: apiBranch.updatedAt,
          };
          return transformedBranch;
        } else {
          const errorMessage = response.error?.message || "Failed to create branch";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      console.error("Create branch error:", error);
      await errorHandlingService.handleApiError(
        error,
        "Create Branch",
        "BranchSlice"
      );
      
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to create branch";
      return rejectWithValue(errorMessage);
    }
  }
);

export const editBranch = createAsyncThunk(
  "branches/editBranch",
  async (
    { id, branchData }: { id: string; branchData: BranchFormData },
    { rejectWithValue }
  ) => {
    try {
    
        const response = await branchService.updateBranch(id, branchData);
        
        if (isSuccessResponse(response)) {
          const apiBranch: any = response.data;        
          return apiBranch;
        } else {
          const errorMessage = response.error?.message || "Failed to update branch";
          console.log("errorMessage from branch slice: ",errorMessage)
          return rejectWithValue(errorMessage);
        }
      
    } catch (error: any) {
      console.error("Update branch error:", error);
      await errorHandlingService.handleApiError(
        error,
        "Edit Branch",
        "BranchSlice"
      );
      
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to update branch";
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeBranch = createAsyncThunk(
  "branches/removeBranch",
  async (id: string, { rejectWithValue }) => {
    try {
      if (USE_STUB_DATA) {
        return id;
      } else {
        const response = await branchService.deleteBranch(id);
        
        if (isSuccessResponse(response)) {
          return id;
        } else {
          const errorMessage = response.error?.message || "Failed to delete branch";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Remove Branch",
        "BranchSlice"
      );
      
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete branch"
      );
    }
  }
);

export const updateBranchStatus = createAsyncThunk(
  "branches/updateBranchStatus",
  async (
    { branchId, status }: { branchId: string; status: boolean },
    { rejectWithValue }
  ) => {
    try {
      // Convert boolean status to string format expected by backend
      const statusString = status ? 'active' : 'inactive';
      const response = await branchService.updateBranchStatus(branchId, statusString);
      
      if (isSuccessResponse(response)) {
        const apiBranch: any = response.data;

        const updatedBranch: Branch = {
          id: apiBranch.id,
          name: apiBranch.name,
          code: apiBranch.code || `BR${apiBranch.id}`,
          address: apiBranch.address,
          city: apiBranch.city,
          state: apiBranch.state,
          zipCode: apiBranch.postal_code || apiBranch.zipCode || "",
          phone: apiBranch.phone,
          email: apiBranch.email,
          status: apiBranch.status === "active" || apiBranch.status,
          managerName: apiBranch.manager?.first_name
            ? `${apiBranch.manager.first_name} ${
                apiBranch.manager.last_name || ""
              }`
            : (apiBranch.managerName || "Not Assigned"),
          managerPhone: apiBranch.managerPhone || "",
          managerEmail:
            apiBranch.manager?.email || apiBranch.managerEmail || "",
          workingHours: apiBranch.workingHours || {
            start: "09:00",
            end: "18:00",
          },
          createdAt: apiBranch.created_at || apiBranch.createdAt,
          updatedAt: apiBranch.updated_at || apiBranch.updatedAt,
        };

        return updatedBranch;
      } else {
        const errorMessage = response.error?.message || "Failed to update status";
        return rejectWithValue(errorMessage);
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Update Branch Status",
        "BranchSlice"
      );
      
      return rejectWithValue(
        error.response?.data?.message || "Failed to update status"
      );
    }
  }
);

const branchSlice = createSlice({
  name: "branches",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setBranches: (state, action: PayloadAction<Branch[]>) => {
      state.branches = action.payload;
    },
    setSelectedBranch: (state, action: PayloadAction<Branch | null>) => {
      state.selectedBranch = action.payload;
    },
    addBranch: (state, action: PayloadAction<Branch>) => {
      state.branches.push(action.payload);
    },
    updateBranch: (state, action: PayloadAction<Branch>) => {
      const index = state.branches.findIndex((b) => b.id === action.payload.id);
      if (index !== -1) {
        state.branches[index] = action.payload;
      }
    },
    deleteBranch: (state, action: PayloadAction<string>) => {
      state.branches = state.branches.filter((b) => b.id !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    clearBranch: (state) => {
      state.selectedBranch = null;
    },
  },
  extraReducers: (builder) => {
    // fetchBranches
    builder
      .addCase(fetchBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = action.payload;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error is now handled centrally - no need for toast here
      });

    // fetchBranchById
    builder
      .addCase(fetchBranchById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranchById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBranch = action.payload;
      })
      .addCase(fetchBranchById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error is now handled centrally - no need for toast here
      });

    // createBranch
    builder
      .addCase(createBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches.push(action.payload);
        // Success message is handled by the centralized system if needed
      })
      .addCase(createBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error is now handled centrally - no need for toast here
      });

    // editBranch
    builder
      .addCase(editBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editBranch.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.branches.findIndex(
          (b: Branch) => b.id === action.payload.id
        );
        if (index !== -1) {
          state.branches[index] = action.payload;
        }
        if (state.selectedBranch?.id === action.payload.id) {
          state.selectedBranch = action.payload;
        }
        // Success message is handled by the centralized system if needed
      })
      .addCase(editBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error is now handled centrally - no need for toast here
      });

    // removeBranch
    builder
      .addCase(removeBranch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeBranch.fulfilled, (state, action) => {
        state.loading = false;
        state.branches = state.branches.filter((b: Branch) => b.id !== action.payload);
        if (state.selectedBranch?.id === action.payload) {
          state.selectedBranch = null;
        }
        // Success message is handled by the centralized system if needed
      })
      .addCase(removeBranch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Error is now handled centrally - no need for toast here
      })
      // updateBranch
      .addCase(updateBranchStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.branches.findIndex((b: Branch) => b.id === updated.id);
        if (index !== -1) {
          state.branches[index] = updated;
        }
        if (state.selectedBranch?.id === updated.id) {
          state.selectedBranch = updated;
        }
        // Success message is handled by the centralized system if needed
      })
      .addCase(updateBranchStatus.rejected, (state, action) => {
        state.error = action.payload as string;
        // Error is now handled centrally - no need for toast here
      });
  },
});

export const {
  setLoading,
  setError,
  setBranches,
  setSelectedBranch,
  addBranch,
  updateBranch,
  deleteBranch,
  clearError,
 
} = branchSlice.actions;

// Selectors
export const selectBranches = (state: any) => state.branches.branches;
export const selectActiveBranches = (state: any) =>
  state.branches.branches.filter((b: Branch) => b.status);
export const selectBranchLoading = (state: any) => state.branches.loading;
export const selectBranchError = (state: any) => state.branches.error;
export const selectSelectedBranch = (state: any) =>state.branches.selectedBranch;

export default branchSlice.reducer;
